import { Injectable } from '@angular/core';
import {
    collection,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    onSnapshot,
    Timestamp,
    CollectionReference,
    DocumentData,
    getDoc,
    arrayRemove,
    arrayUnion
} from 'firebase/firestore';
import { FirebaseService } from './firebase.service';
import { UserService } from './user.service';
import { v4 as uuidv4 } from 'uuid';
import { environment } from 'env/environment';

@Injectable({ providedIn: 'root' })
export class FlightConnectionChatService {
    constructor(
        private firebaseService: FirebaseService,
        private userService: UserService
    ) { }

    private getMessagesRef(connectionId: string): CollectionReference<DocumentData> {
        return collection(this.firebaseService.firestore, `flight-connections/${connectionId}/messages`);
    }

    async hasUnreadNotification(connectionId: string): Promise<boolean> {
        const user = await this.userService.getUserProfile();
        const notificationRef = doc(
            this.firebaseService.firestore,
            `users/${user.id}/notifications/${connectionId}`
        );
        const snap = await getDoc(notificationRef);

        if (!snap.exists()) {
            // No notification doc at all → nothing unread
            return false;
        }

        const data = snap.data() as {
            newCount?: number;
            seen?: boolean;
        };

        // treat any positive newCount as “unread”
        return (data.newCount ?? 0) > 0;
    }

    async sendMessage(connectionId: string, text: string, replyToId: string | null = null, fromUserName: string, toUserId: string): Promise<void> {
        const user = await this.userService.getUserProfile();
        const messageId = uuidv4();
        const messageRef = doc(this.getMessagesRef(connectionId), messageId);

        // Extract first URL if present
        const urlMatch = text.match(/https?:\/\/[^\s]+/);
        const sharedUrl = urlMatch ? urlMatch[0] : null;

        const messageData: any = {
            messageId,
            userId: user.id,
            text,
            replyToId: replyToId ?? null,
            timestamp: Timestamp.now(),
            unsent: false,
        };

        if (sharedUrl) {
            messageData.sharedUrl = sharedUrl;
            fetch(`https://${environment.apiBaseURL}.cloudfunctions.net/extractOGMetadata`, {
                method: 'POST',
                body: JSON.stringify({ sharedUrl, connectionId, messageId }),
                headers: {
                    'Content-Type': 'application/json'
                }
            }).catch(() => {
                // fire and forget
                console.warn('Unable to fetch og data for shared url')
            });
        }

        await setDoc(messageRef, messageData);
        await this.createOrUpdateNotification(connectionId, fromUserName, toUserId);
    }

    subscribeToMessages(
        connectionId: string,
        onUpdate: (messages: any[]) => void
    ): () => void {
        const q = query(this.getMessagesRef(connectionId), orderBy('timestamp', 'asc'));

        return onSnapshot(q, (snapshot) => {
            const messages = snapshot.docs.map(doc => doc.data());
            onUpdate(messages);
        });
    }

    async unsendMessage(connectionId: string, messageId: string): Promise<void> {
        const msgRef = doc(this.getMessagesRef(connectionId), messageId);
        await updateDoc(msgRef, {
            text: '[message unsent]',
            unsent: true
        });
    }

    async setTypingState(connectionId: string, userId: string, isTyping: boolean): Promise<void> {
        const docRef = doc(this.firebaseService.firestore, 'flight-connections', connectionId);
        await updateDoc(docRef, {
            usersTyping: isTyping ? arrayUnion(userId) : arrayRemove(userId)
        });
    }

    async createOrUpdateNotification(connectionId: string, fromUserName: string, toUserId: string): Promise<void> {
        const notificationRef = doc(
            this.firebaseService.firestore,
            `users/${toUserId}/notifications/${connectionId}`
        );

        const existingSnap = await getDoc(notificationRef);
        const now = new Date();

        if (existingSnap.exists()) {
            const existingData = existingSnap.data();
            await updateDoc(notificationRef, {
                newCount: (existingData.newCount || 0) + 1,
                updatedAt: now,
                emailedOn: null,
                seen: false,
            });
        } else {
            await setDoc(notificationRef, {
                createdAt: now,
                updatedAt: now,
                emailedOn: null,
                watcherId: null,
                notificationFlightOptions: null,
                type: 'new-messages',
                fromUserName: fromUserName,
                connectionId,
                message: `New message in your conversation with ${fromUserName}`,
                newCount: 1,
                seen: false
            });
        }
    }


    async markNotificationAsSeen(connectionId: string): Promise<void> {
        const user = await this.userService.getUserProfile();
        const notificationRef = doc(this.firebaseService.firestore, `users/${user.id}/notifications/${connectionId}`);
        const snap = await getDoc(notificationRef);
        if (snap.exists()) {
            await updateDoc(notificationRef, {
                seen: true,
                newCount: 0
            });
        }
    }
}
